import { CommonModule, isPlatformServer } from '@angular/common';
import type {
  AfterViewChecked,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  PLATFORM_ID,
  ViewEncapsulation,
} from '@angular/core';
import { FileDownload } from '@freelancer/file-download';
import { LightgalleryModule } from 'lightgallery/angular';
import type { InitDetail } from 'lightgallery/lg-events';
import type { LightGallerySettings } from 'lightgallery/lg-settings';
import type { GalleryItem as LightGalleryItem } from 'lightgallery/lg-utils';
import type { LightGallery } from 'lightgallery/lightgallery';
import lgFullscreen from 'lightgallery/plugins/fullscreen';
import lgRotate from 'lightgallery/plugins/rotate';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgVideo from 'lightgallery/plugins/video';
import lgZoom from 'lightgallery/plugins/zoom';
import type { Subscription } from 'rxjs';
import { fromEvent } from 'rxjs';
import {
  MediaViewerMode,
  MediaViewerSlideTransition,
} from '../media-viewer.types';
import { LIGHTGALLERY_CONFIG } from './media-viewer-light-gallery.config';
import {
  LightGalleryConfig,
  lightGalleryItemSelector,
  lightGalleryThumbnailProperty,
  lightGalleryTransitionMap,
} from './media-viewer-light-gallery.types';

@Component({
  standalone: true,
  selector: 'fl-media-viewer-light-gallery',
  imports: [CommonModule, LightgalleryModule],
  template: `
    <ng-container *ngIf="isServer; else clientRendering">
      <ng-container [ngTemplateOutlet]="content"></ng-container>
    </ng-container>

    <ng-template #clientRendering>
      <lightgallery
        [settings]="lightGallerySettings"
        [onInit]="onLightGalleryInit"
      >
        <ng-container [ngTemplateOutlet]="content"></ng-container>
      </lightgallery>
    </ng-template>

    <ng-template #content>
      <ng-content></ng-content>
    </ng-template>
  `,
  styleUrls: ['./media-viewer-light-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
})
export class MediaViewerLightGalleryComponent
  implements OnChanges, OnInit, AfterViewChecked, OnDestroy
{
  @Input() mode: MediaViewerMode;
  @Input() lightGalleryItems?: readonly LightGalleryItem[];

  @Input() enableFullscreen: boolean;
  @Input() enablePhotoRotations: boolean;
  @Input() shouldLoopBack: boolean;
  @Input() showCloseButton: boolean;
  @Input() showCounter: boolean;
  @Input() showDownloadButton: boolean;
  @Input() slideTransition: MediaViewerSlideTransition;

  @Input() ariaDescribedBy?: string;
  @Input() ariaLabelledBy?: string;

  @Input() itemSelector = lightGalleryItemSelector;

  lightGallery: LightGallery;
  lightGallerySettings: LightGallerySettings = {
    plugins: [lgZoom, lgThumbnail, lgFullscreen, lgRotate, lgVideo],
    exThumbImage: lightGalleryThumbnailProperty,
    speed: 200,
    enableZoomAfter: 200,
    rotateSpeed: 200,
    selector: this.itemSelector,
    strings: {
      closeGallery: $localize`Close gallery`,
      toggleMaximize: $localize`Toggle maximize`,
      previousSlide: $localize`Previous slide`,
      nextSlide: $localize`Next slide`,
      download: $localize`Download`,
      playVideo: $localize`Play video`,
    },
    rotatePluginStrings: {
      flipVertical: $localize`Flip vertical`,
      flipHorizontal: $localize`Flip horizontal`,
      rotateLeft: $localize`Rotate left`,
      rotateRight: $localize`Rotate right`,
    },
    fullscreenPluginStrings: { toggleFullscreen: $localize`Toggle Fullscreen` },
    hideScrollbar: true,
    scale: 0.25,
  };
  isServer: boolean;
  private isLightGalleryRefreshNeeded = false;
  private downloadSubscription: Subscription;

  constructor(
    @Inject(LIGHTGALLERY_CONFIG) private config: LightGalleryConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
    private fileDownload: FileDownload,
  ) {}

  // Callback for lightgallery onInit event.
  // This needs to be an anonymous function, to be able to access the `this` context.
  onLightGalleryInit = (details: InitDetail): void => {
    this.lightGallery = details.instance;
    const downloadButton = this.lightGallery.$toolbar
      .get()
      .getElementsByClassName('lg-download')
      .item(0);

    if (downloadButton) {
      this.downloadSubscription = fromEvent(downloadButton, 'click').subscribe(
        event => {
          // Let light gallery handle the click event if:
          // 1. The target is not an anchor element.
          // 2. The target does not have a href attribute.
          // 3. The lightGalleryItems are not defined.
          const target = event.target as HTMLAnchorElement;
          if (!target || !target.href || !this.lightGalleryItems) {
            return;
          }

          const targetUrl = target.href;
          const foundItem = this.lightGalleryItems.find(
            item => item.downloadUrl === targetUrl,
          );

          // If we can't find the item in the lightGalleryItems, let light gallery handle the click event.
          if (!foundItem) {
            return;
          }

          // Stop light gallery from handling the click event.
          event.preventDefault();
          event.stopImmediatePropagation();

          // Download the file manually.
          this.fileDownload.download(target.href, foundItem.fileName);
        },
      );
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if ('lightGalleryItems' in changes) {
      this.isLightGalleryRefreshNeeded = true;
    }
  }

  ngOnInit(): void {
    this.isServer = isPlatformServer(this.platformId);
    this.lightGallerySettings = {
      licenseKey: this.config.licenseKey,
      ...this.lightGallerySettings,
      dynamic: this.mode === 'modal',
      counter: this.showCounter,
      download: this.showDownloadButton,
      loop: this.shouldLoopBack,
      mode: lightGalleryTransitionMap[this.slideTransition],
      showCloseIcon: this.showCloseButton,
      flipHorizontal: this.enablePhotoRotations,
      flipVertical: this.enablePhotoRotations,
      rotate: this.enablePhotoRotations,
      rotateLeft: this.enablePhotoRotations,
      rotateRight: this.enablePhotoRotations,
      ariaDescribedby: this.ariaDescribedBy,
      ariaLabelledby: this.ariaLabelledBy,
      fullScreen: this.enableFullscreen,
      mobileSettings: {
        controls: true,
        download: this.showDownloadButton,
        showCloseIcon: true,
      },
    };
  }

  ngAfterViewChecked(): void {
    // modal mode should only refresh when opened, otherwise full resolution items will download on page load
    if (
      this.mode === 'inline' &&
      this.isLightGalleryRefreshNeeded &&
      !this.isServer
    ) {
      this.isLightGalleryRefreshNeeded = false;
      this.lightGallery.refresh(
        this.lightGalleryItems ? [...this.lightGalleryItems] : undefined,
      );
    }
  }

  goToItem(index: number): void {
    this.lightGallery.slide(index);
  }

  openModal(index = 0): void {
    if (this.isLightGalleryRefreshNeeded) {
      this.lightGallery.refresh(
        this.lightGalleryItems ? [...this.lightGalleryItems] : undefined,
      );
    }

    if (this.lightGalleryItems && index < this.lightGalleryItems.length) {
      this.lightGallery.openGallery(index);
    }
  }

  isModalOpen(): boolean {
    return this.lightGallery.lgOpened;
  }

  ngOnDestroy(): void {
    this.downloadSubscription?.unsubscribe();
  }
}
